import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, ShoppingBag, DollarSign, Sparkles, Activity, HardDrive, ShieldCheck, RefreshCw, AlertCircle } from "lucide-react";
import LoginActivityAlert from "@/components/LoginActivityAlert";

function Kpi({ label, value, icon: Icon, tone = "text-primary", suffix }) {
  return (
    <div className="surface-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="label-overline">{label}</span>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">{value ?? "—"}{suffix ? <span className="text-base font-bold text-muted-foreground ml-1">{suffix}</span> : null}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [k, setK] = useState(null);

  useEffect(() => {
    api.get("/admin/platform-kpis").then(({ data }) => setK(data));
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="label-overline">Projexino HQ · Super Admin</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">{t("dashboards.admin.welcome")}</h1>
        <p className="text-muted-foreground mt-2">{t("dashboards.admin.subtitle")}</p>
      </div>

      <LoginActivityAlert />

      {/* Per-portal tenant counts */}
      <div>
        <h2 className="font-display text-xl font-bold mb-3">Tenants by portal</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <Kpi label="Organizations" value={k?.portals?.organizations} icon={Building2} tone="text-emerald-600" />
          <Kpi label="Vendors" value={k?.portals?.vendors} icon={ShoppingBag} tone="text-pink-600" />
          <Kpi label="Distributors" value={k?.portals?.distributors} icon={Activity} tone="text-blue-600" />
        </div>
      </div>

      {/* Organization KPIs */}
      <div>
        <h2 className="font-display text-xl font-bold mb-3">Organizations</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Total organizations" value={k?.orgs?.total} icon={Building2} />
          <Kpi label="Active" value={k?.orgs?.active} icon={ShieldCheck} tone="text-emerald-600" />
          <Kpi label="Trial" value={k?.orgs?.trial} icon={Sparkles} tone="text-violet-600" />
          <Kpi label="Pending approval" value={k?.orgs?.pending} icon={AlertCircle} tone="text-amber-600" />
          <Kpi label="Suspended" value={k?.orgs?.suspended} icon={AlertCircle} tone="text-orange-600" />
          <Kpi label="Expired" value={k?.orgs?.expired} icon={AlertCircle} tone="text-red-600" />
          <Kpi label="Renewals due (7d)" value={k?.renewals_due_7d} icon={RefreshCw} tone="text-amber-700" />
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Users & marketplace</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Total users" value={k?.users?.total} icon={Users} />
          <Kpi label="Vendors / dealers" value={k?.users?.vendors} icon={Users} tone="text-accent" />
          <Kpi label="Buyers / customers" value={k?.users?.buyers} icon={Users} tone="text-accent" />
          <Kpi label="Products" value={k?.marketplace?.products} icon={ShoppingBag} />
          <Kpi label="Orders" value={k?.marketplace?.orders} icon={ShoppingBag} />
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Revenue & platform health</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="MRR" value={`₹${(k?.revenue?.mrr ?? 0).toLocaleString()}`} icon={DollarSign} tone="text-emerald-700" />
          <Kpi label="ARR" value={`₹${(k?.revenue?.arr ?? 0).toLocaleString()}`} icon={DollarSign} tone="text-emerald-700" />
          <Kpi label="Today's revenue" value={`₹${(k?.revenue?.today ?? 0).toLocaleString()}`} icon={DollarSign} />
          <Kpi label="Payment success" value={`${k?.payment_success_rate ?? 0}`} suffix="%" icon={ShieldCheck} tone="text-emerald-600" />
          <Kpi label="AI requests today" value={k?.ai_requests_today} icon={Sparkles} tone="text-violet-600" />
          <Kpi label="API calls today" value={k?.api_calls_today} icon={Activity} />
          <Kpi label="Active sessions" value={k?.active_sessions} icon={Activity} />
          <Kpi label="Storage used" value={`${k?.storage_usage_mb ?? 0}`} suffix="MB" icon={HardDrive} />
        </div>
      </div>

      {/* Recent activity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent activity</CardTitle>
          <Badge variant="outline">Audit feed</Badge>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(k?.recent_activity || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-b last:border-b-0 py-2">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="font-mono text-[10px]">{a.action}</Badge>
                  <span className="text-muted-foreground">{a.actor_email}</span>
                  <span className="text-muted-foreground">→ {a.entity_type}</span>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
            {(!k?.recent_activity || k.recent_activity.length === 0) && (
              <div className="text-center text-muted-foreground py-6">No recent activity.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
